// useSubscription Hook
// Manages subscription state, limits, and feature access control

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';

export type SubscriptionPlan = 'free_trial' | 'silver' | 'gold' | 'admin';
export type SubscriptionStatus = 'active' | 'expired' | 'pending_approval' | 'blocked';

export interface SubscriptionLimits {
  maxCharts: number;
  maxChatGeneral: number; // per 30 days
  maxChatLifeAreas: number; // per 30 days
  canAccessFamily: boolean;
  canAccessPredictions: boolean;
  canAccessYogas: boolean;
  canAccessDivisionalCharts: boolean;
  canAccessFullAnalysis: boolean;
  canAccessEmailSupport: boolean;
  canAccessReminders: boolean;
}

export interface SubscriptionInfo {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  trialStartDate: string | null;
  trialEndDate: string | null;
  daysRemaining: number | null;
  limits: SubscriptionLimits;
}

export interface UsageStats {
  chartsCount: number;
  chatGeneralCount: number;
  chatLifeAreasUsed: string[];
  chatLifeAreasCount: number;
}

const PLAN_LIMITS: Record<SubscriptionPlan, SubscriptionLimits> = {
  free_trial: {
    maxCharts: 4,
    maxChatGeneral: 0,
    maxChatLifeAreas: 0,
    canAccessFamily: true,
    canAccessPredictions: true,
    canAccessYogas: true,
    canAccessDivisionalCharts: true,
    canAccessFullAnalysis: true,
    canAccessEmailSupport: false,
    canAccessReminders: false,
  },
  silver: {
    maxCharts: 4, // Self + Spouse + 2 family (matches landing page)
    maxChatGeneral: 3,
    maxChatLifeAreas: 3,
    canAccessFamily: true,
    canAccessPredictions: true,
    canAccessYogas: true,
    canAccessDivisionalCharts: true,
    canAccessFullAnalysis: true,
    canAccessEmailSupport: false,
    canAccessReminders: false,
  },
  gold: {
    maxCharts: 6, // Self + Spouse + 4 family (matches landing page)
    maxChatGeneral: 6,
    maxChatLifeAreas: 6,
    canAccessFamily: true,
    canAccessPredictions: true,
    canAccessYogas: true,
    canAccessDivisionalCharts: true,
    canAccessFullAnalysis: true,
    canAccessEmailSupport: true,
    canAccessReminders: true,
  },
  admin: {
    maxCharts: 999999,
    maxChatGeneral: 999999,
    maxChatLifeAreas: 999999,
    canAccessFamily: true,
    canAccessPredictions: true,
    canAccessYogas: true,
    canAccessDivisionalCharts: true,
    canAccessFullAnalysis: true,
    canAccessEmailSupport: true,
    canAccessReminders: true,
  },
};

export interface ChatCreditsInfo {
  total: number;    // yearly base + purchased
  used: number;     // messages sent since subscription start
  remaining: number;
}

export function useSubscription(userId: string | null) {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [chatCredits, setChatCredits] = useState<ChatCreditsInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!userId) {
      setSubscription(null);
      setUsage(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Check if user is admin first (with 5-minute cache)
      const profileCacheKey = `profile_role_${userId}_v1`;
      const profileCachedStr = localStorage.getItem(profileCacheKey);
      let profile: { role: string } | null = null;
      
      if (profileCachedStr) {
        try {
          const parsed = JSON.parse(profileCachedStr);
          const age = Date.now() - (parsed.timestamp || 0);
          if (age < 5 * 60 * 1000) { // 5 minutes
            profile = parsed.data;
          }
        } catch (e) { }
      }
      
      if (!profile) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', userId)
          .single();

        if (profileError) throw profileError;
        profile = profileData as { role: string };
        
        if (profile) {
          localStorage.setItem(profileCacheKey, JSON.stringify({ data: profile, timestamp: Date.now() }));
        }
      }

      // Admins get full access
      if (profile.role === 'admin') {
        setSubscription({
          plan: 'admin',
          status: 'active',
          trialStartDate: null,
          trialEndDate: null,
          daysRemaining: null,
          limits: PLAN_LIMITS.admin,
        });
        // For admin: fetch purchased credits for testing visibility
        const { data: adminPurchases } = await supabase
          .from('chat_credit_purchases')
          .select('credits_count')
          .eq('user_id', userId)
          .eq('status', 'success');
        const purchasedCredits = (adminPurchases || []).reduce((sum: number, p: any) => sum + (p.credits_count || 0), 0);
        setChatCredits({ total: 999999 + purchasedCredits, used: 0, remaining: 999999 });
        setLoading(false);
        return;
      }

      // Get subscription info from user_subscriptions table (with 2-minute cache)
      const subCacheKey = `user_subscription_${userId}_v2`;
      const subCachedStr = localStorage.getItem(subCacheKey);
      let subscription: any = null;
      let subError: any = null;
      
      if (subCachedStr) {
        try {
          const parsed = JSON.parse(subCachedStr);
          const age = Date.now() - (parsed.timestamp || 0);
          if (age < 2 * 60 * 1000) { // 2 minutes
            subscription = parsed.data;
          }
        } catch (e) { }
      }
      
      if (!subscription) {
        // Prefer active/trial over pending_payment; order by created_at desc to get latest
        const result = await supabase
          .from('user_subscriptions')
          .select('*, subscription_plans(*)')
          .eq('user_id', userId)
          .not('status', 'eq', 'pending_payment')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        // If no non-pending record, fall back to latest pending_payment
        if (!result.data && !result.error) {
          const pendingResult = await supabase
            .from('user_subscriptions')
            .select('*, subscription_plans(*)')
            .eq('user_id', userId)
            .eq('status', 'pending_payment')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          subscription = pendingResult.data;
          subError = pendingResult.error;
        } else {
          subscription = result.data;
          subError = result.error;
        }
        
        if (subscription) {
          localStorage.setItem(subCacheKey, JSON.stringify({ data: subscription, timestamp: Date.now() }));
        }
      }

      if (subError || !subscription) {
        // No subscription found - check if user has pending approval request
        const { data: accessRequest } = await supabase
          .from('access_requests')
          .select('status, request_type')
          .eq('user_id', userId)
          .eq('status', 'pending')
          .eq('request_type', 'trial_approval')
          .single();
        
        if (accessRequest) {
          // User is awaiting admin approval (previously deleted user)
          setSubscription({
            plan: 'free_trial',
            status: 'pending_approval',
            trialStartDate: null,
            trialEndDate: null,
            daysRemaining: null,
            limits: PLAN_LIMITS.free_trial,
          });
          setLoading(false);
          return;
        }
        
        // No subscription and no pending request - default to free trial
        setSubscription({
          plan: 'free_trial',
          status: 'active',
          trialStartDate: null,
          trialEndDate: null,
          daysRemaining: null,
          limits: PLAN_LIMITS.free_trial,
        });
        setLoading(false);
        return;
      }

      // Map database status to SubscriptionStatus
      let status: SubscriptionStatus = 'active';
      const now = new Date();
      
      if (subscription.status === 'expired' || subscription.status === 'cancelled') {
        status = 'expired';
      } else if (subscription.status === 'payment_failed') {
        status = 'blocked';
      } else if (subscription.status === 'trial' && subscription.trial_end_date) {
        const trialEndDate = new Date(subscription.trial_end_date);
        if (now > trialEndDate) {
          // Trial period over — if a paid subscription_end_date exists and is still valid,
          // the user is in their paid subscription period (not expired)
          if (subscription.subscription_end_date) {
            const subEndDate = new Date(subscription.subscription_end_date);
            status = now <= subEndDate ? 'active' : 'expired';
          } else {
            status = 'expired'; // Pure free trial with no paid subscription
          }
        } else {
          status = 'active';
        }
      } else if (subscription.status === 'trial' && !subscription.trial_end_date) {
        // trial_end_date was never set (trigger failed) — treat as 7-day trial from creation
        const trialStart = new Date(subscription.created_at);
        const inferredEnd = new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000);
        status = now > inferredEnd ? 'expired' : 'active';
      } else if (subscription.status === 'active' && subscription.subscription_end_date) {
        // Check if paid subscription has expired
        const subEndDate = new Date(subscription.subscription_end_date);
        if (now > subEndDate) {
          status = 'expired';
        } else {
          status = 'active';
        }
      }

      // Determine plan from subscription_plans table
      const planName = subscription.subscription_plans?.plan_name as 'silver' | 'gold';
      const plan = planName || 'free_trial';

      // Calculate days remaining
      let daysRemaining: number | null = null;
      
      if (subscription.status === 'trial' && subscription.trial_end_date) {
        const trialEndDate = new Date(subscription.trial_end_date);
        if (now <= trialEndDate) {
          // Still in trial period
          daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        } else if (subscription.subscription_end_date) {
          // Trial ended, now in paid subscription period
          const endDate = new Date(subscription.subscription_end_date);
          daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        }
      } else if (subscription.status === 'active' && subscription.subscription_end_date) {
        const endDate = new Date(subscription.subscription_end_date);
        daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      }

      // Get limits from subscription_plans or use defaults
      const limits: SubscriptionLimits = subscription.subscription_plans ? {
        maxCharts: subscription.subscription_plans.max_charts,
        maxChatGeneral: subscription.subscription_plans.max_chat_general,
        maxChatLifeAreas: subscription.subscription_plans.max_chat_life_areas,
        canAccessFamily: subscription.subscription_plans.can_access_family,
        canAccessPredictions: subscription.subscription_plans.can_access_predictions,
        canAccessYogas: subscription.subscription_plans.can_access_yogas,
        canAccessDivisionalCharts: subscription.subscription_plans.can_access_divisional_charts,
        canAccessFullAnalysis: subscription.subscription_plans.can_access_full_analysis,
        canAccessEmailSupport: subscription.subscription_plans.can_access_email_support,
        canAccessReminders: subscription.subscription_plans.can_access_reminders,
      } : PLAN_LIMITS[plan];

      setSubscription({
        plan,
        status,
        trialStartDate: subscription.trial_start_date,
        trialEndDate: subscription.trial_end_date,
        daysRemaining,
        limits,
      });

      // Get usage statistics
      const subscriptionStartDate = subscription.subscription_start_date ||
        subscription.trial_start_date ||
        new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();

      const [chartsResult, chatResult, purchasedCreditsResult] = await Promise.all([
        supabase
          .from('birth_profiles')
          .select('id', { count: 'exact', head: true })
          .eq('owner_id', userId),
        supabase
          .from('chat_messages')
          .select('question_type')
          .eq('user_id', userId)
          .eq('role', 'user')
          .gte('created_at', subscriptionStartDate),
        supabase
          .from('chat_credit_purchases')
          .select('credits_count')
          .eq('user_id', userId)
          .eq('status', 'success'),
      ]);

      const chartsCount = chartsResult.count || 0;
      const messages = chatResult.data || [];

      const generalMessages = messages.filter((m: any) => m.question_type === 'general' || !m.question_type);
      const lifeAreaMessages = messages.filter((m: any) => m.question_type === 'life_area');

      setUsage({
        chartsCount,
        chatGeneralCount: generalMessages.length,
        chatLifeAreasUsed: [], // Not tracking individual life areas anymore, just count
        chatLifeAreasCount: lifeAreaMessages.length,
      });

      // Compute chat credits
      const yearlyBase: number = subscription.subscription_plans?.yearly_chat_credits ?? 0;
      const purchased = (purchasedCreditsResult.data || []).reduce(
        (sum: number, p: any) => sum + (p.credits_count || 0), 0
      );
      const totalCredits = yearlyBase + purchased;
      const usedCredits = messages.length;
      setChatCredits({
        total: totalCredits,
        used: usedCredits,
        remaining: Math.max(0, totalCredits - usedCredits),
      });

      setError(null);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const canAccess = useCallback(
    (feature: keyof SubscriptionLimits): boolean => {
      if (!subscription) return false;
      if (subscription.status !== 'active') return false;
      return subscription.limits[feature] as boolean;
    },
    [subscription]
  );

  const canCreateChart = useCallback((): boolean => {
    if (!subscription) return false;
    
    // Admins have unlimited access
    if (subscription.plan === 'admin') return true;
    
    if (!usage) return false;
    if (subscription.status !== 'active') return false;
    return usage.chartsCount < subscription.limits.maxCharts;
  }, [subscription, usage]);

  const canAskChatQuestion = useCallback(
    (type: 'general' | 'life_area'): boolean => {
      if (!subscription) return false;
      
      // Admins have unlimited access
      if (subscription.plan === 'admin') return true;
      
      if (!usage) return false;
      if (subscription.status !== 'active') return false;

      // Use chat credits if available, otherwise fall back to legacy monthly limits
      if (chatCredits && chatCredits.total > 0) {
        return chatCredits.remaining > 0;
      }

      if (type === 'general') {
        return usage.chatGeneralCount < subscription.limits.maxChatGeneral;
      } else {
        return usage.chatLifeAreasCount < subscription.limits.maxChatLifeAreas;
      }
    },
    [subscription, usage, chatCredits]
  );

  const getRemainingQuota = useCallback(
    (type: 'charts' | 'chat_general' | 'chat_life_area' | 'chat_credits'): number => {
      if (!subscription) return 0;
      
      // Admins have unlimited quota
      if (subscription.plan === 'admin') return 999999;
      
      if (!usage) return 0;

      if (type === 'charts') {
        return Math.max(0, subscription.limits.maxCharts - usage.chartsCount);
      } else if (type === 'chat_credits') {
        return chatCredits ? chatCredits.remaining : 0;
      } else if (type === 'chat_general') {
        return Math.max(0, subscription.limits.maxChatGeneral - usage.chatGeneralCount);
      } else {
        return Math.max(0, subscription.limits.maxChatLifeAreas - usage.chatLifeAreasCount);
      }
    },
    [subscription, usage, chatCredits]
  );

  const getUpgradeMessage = useCallback(
    (feature: string): string => {
      if (!subscription) return 'Loading...';

      if (subscription.status === 'expired') {
        return '⏰ Your trial has expired. Upgrade to Silver or Gold to continue.';
      }

      if (subscription.status === 'pending_approval') {
        return '⏳ Your trial request is pending admin approval.';
      }

      if (subscription.plan === 'free_trial') {
        // Special message for AI chat features
        if (feature.toLowerCase().includes('chat') || feature.toLowerCase().includes('ai')) {
          return '🤖 AI Chat is available only for Silver & Gold members. Upgrade now to unlock unlimited AI-powered insights!';
        }
        return `🔒 ${feature} is not available in free trial. Upgrade to Silver or Gold to unlock this feature.`;
      }

      if (subscription.plan === 'silver') {
        return `🔒 ${feature} requires Gold subscription. Upgrade to unlock advanced features.`;
      }

      return 'Feature not available in your current plan.';
    },
    [subscription]
  );

  // Force-refresh: clears both localStorage caches first so stale data
  // never prevents the overlay from dismissing after a successful payment.
  const forceRefresh = useCallback(async () => {
    if (userId) {
      localStorage.removeItem(`user_subscription_${userId}_v2`);
      localStorage.removeItem(`profile_role_${userId}_v1`);
    }
    await fetchSubscription();
  }, [userId, fetchSubscription]);

  return {
    subscription,
    usage,
    chatCredits,
    loading,
    error,
    canAccess,
    canCreateChart,
    canAskChatQuestion,
    getRemainingQuota,
    getUpgradeMessage,
    refresh: fetchSubscription,
    forceRefresh,
  };
}
